import { useQuery, useQueryClient } from "@tanstack/react-query";
import React, { useState, useRef } from "react";
import { axiosget, axiospost, axiosput } from "../../helpers/httprequest";
import is_money from "../../helpers/is_money";
import swal from "../../helpers/swal";
import Swal from "sweetalert2";
import ReactModal from "react-modal";
import { motion } from "framer-motion";
import { FiGift, FiX, FiInfo } from "react-icons/fi";

const PayAcceptance = ({ v }) => {
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [showWaiverModal, setShowWaiverModal] = useState(false);
  const [waiverCodes, setWaiverCodes] = useState(["", "", "", "", "", ""]);
  const [waiverLoading, setWaiverLoading] = useState(false);
  const waiverInputRefs = useRef([]);

  // Fetch acceptance fee items from backend
  const { data: feeItems, isLoading, error } = useQuery({
    queryKey: ["acceptanceFee"],
    queryFn: async () => {
      const req = await axiosget(`ms_acceptance_fee`);
      return req?.data;
    },
  });

  // Fetch payment gateway info
  const { data: gatewayInfo } = useQuery({
    queryKey: ["paymentGateway"],
    queryFn: async () => {
      const req = await axiosget(`ms_payments/gateway`);
      return req?.data;
    },
  });

  // Calculate total amount from fee items
  const totalAmount = feeItems?.reduce((total, item) => total + (item.amount || 0), 0) || 0;
  const gateway = gatewayInfo?.data;

  const handlePayment = async () => {
    try {
      setLoading(true);

      if (!feeItems || feeItems.length === 0) {
        swal("error", "No acceptance fee has been configured. Please contact the administrator.");
        setLoading(false);
        return;
      }

      // Initialize payment via unified gateway
      const response = await axiospost("ms_payments/initialize", {
        feeType: "acceptance_fee",
        amount: totalAmount,
        payerEmail: v?.applicant?.emailaddress,
        payerName: `${v?.applicant?.firstname || ""} ${v?.applicant?.middlename || ""} ${v?.applicant?.lastname || ""}`.trim(),
        payerPhone: v?.applicant?.phonenumber || "",
        applicantId: v?.applicant?._id,
        metadata: {
          applicationId: v?._id,
          applicationCode: v?.code,
          programmeId: v?.programme?._id,
          programmeName: v?.programme?.name,
          feeItems: feeItems.map(item => ({
            description: item.description,
            amount: item.amount
          }))
        }
      });

      setLoading(false);

      if (response?.data?.success) {
        const paymentData = response.data.data;

        // Store the transaction reference and link to application
        const saveResult = await axiospost("ms_acceptance_fee_rrr", {
          application: v?._id,
          rrr: paymentData.gatewayReference || paymentData.reference,
          rrrdata: paymentData,
        });

        // Check if save was successful
        if (saveResult?.data?.type === "error" || saveResult?.type === "error") {
          swal("error", saveResult?.data?.message || saveResult?.message || "Failed to save payment reference");
          return;
        }

        console.log("Acceptance fee saved:", saveResult);

        // Show modal with Pay Now / Pay Later options
        const result = await Swal.fire({
          icon: "success",
          title: "Payment Reference Generated!",
          html: `
            <div class="text-center">
              <p class="text-neutral-600 mb-4">Your acceptance fee payment has been initiated via <strong>${gateway?.displayName || paymentData.gateway}</strong></p>
              ${paymentData.gatewayReference ? `
                <div class="bg-primary-50 border-2 border-primary-200 rounded-lg p-4 mb-4">
                  <p class="text-sm font-medium text-primary-900 mb-2">Payment Reference</p>
                  <p class="text-xl font-bold text-primary-600 font-mono">${paymentData.gatewayReference}</p>
                </div>
              ` : ""}
              <p class="text-sm text-neutral-500">Click "Pay Now" to proceed to payment or "Pay Later" to return later</p>
            </div>
          `,
          confirmButtonText: "Pay Now",
          showCancelButton: true,
          cancelButtonText: "Pay Later",
          customClass: {
            confirmButton: "bg-primary-600 hover:bg-primary-700 text-white font-medium py-2 px-6 rounded-lg",
            cancelButton: "bg-neutral-200 hover:bg-neutral-300 text-neutral-700 font-medium py-2 px-6 rounded-lg ml-2",
          },
        });

        // If user clicks "Pay Now", open payment URL in new tab
        if (result.isConfirmed && paymentData.paymentUrl) {
          window.open(paymentData.paymentUrl, "_blank");
        }

        // Refresh data AFTER modal closes (invalidate and refetch)
        await queryClient.refetchQueries({ queryKey: ["acceptanceInformation"] });
      } else {
        swal("error", response?.data?.message || "Failed to initialize payment. Please try again.");
      }
    } catch (err) {
      swal("error", err.message || "An error occurred while processing payment.");
      setLoading(false);
    }
  };

  const handleWaiverCodeChange = (index, value) => {
    const sanitized = value.toUpperCase().replace(/[^A-Z0-9]/g, "");
    if (sanitized.length <= 1) {
      const newCodes = [...waiverCodes];
      newCodes[index] = sanitized;
      setWaiverCodes(newCodes);
      if (sanitized && index < 5) {
        waiverInputRefs.current[index + 1]?.focus();
      }
    }
  };

  const handleWaiverKeyDown = (index, e) => {
    if (e.key === "Backspace" && !waiverCodes[index] && index > 0) {
      waiverInputRefs.current[index - 1]?.focus();
    }
  };

  const handleWaiverPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 6);
    const newCodes = pastedData.split("").concat(Array(6).fill("")).slice(0, 6);
    setWaiverCodes(newCodes);
    const nextEmptyIndex = newCodes.findIndex(code => !code);
    if (nextEmptyIndex !== -1) {
      waiverInputRefs.current[nextEmptyIndex]?.focus();
    } else {
      waiverInputRefs.current[5]?.focus();
    }
  };

  const handleApplyWaiver = async () => {
    try {
      const waiverCode = waiverCodes.join("");
      if (waiverCode.length !== 6) {
        swal("error", "Please enter a valid 6-character waiver code");
        return;
      }
      setWaiverLoading(true);
      const req = await axiosput(`ms_waiver/${waiverCode}/acceptance`, {
        applicationId: v?._id,
      });
      const result = req?.data;
      if (result?.type === "success") {
        swal("success", result.message);
        setShowWaiverModal(false);
        setWaiverCodes(["", "", "", "", "", ""]);
        queryClient.refetchQueries({ queryKey: ["acceptanceInformation"] });
      } else {
        swal("error", result?.message || "Failed to apply waiver code");
      }
      setWaiverLoading(false);
    } catch (error) {
      swal("error", error?.message || "An error occurred while applying the waiver code");
      setWaiverLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      {isLoading && <p className="text-sm text-gray-500">Loading fee information...</p>}
      {error && <p className="text-sm text-red-500">Error: Unable to load fee information</p>}

      {feeItems && feeItems.length > 0 && (
        <>
          {/* Fee breakdown */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Acceptance Fee Breakdown</h4>
            <div className="space-y-1">
              {feeItems.map((item, index) => (
                <div key={index} className="flex justify-between text-sm">
                  <span className="text-gray-600">{item.description}</span>
                  <span className="font-medium">{is_money(item.amount)}</span>
                </div>
              ))}
              <div className="border-t pt-2 mt-2 flex justify-between font-semibold">
                <span>Total</span>
                <span className="text-green-600">{is_money(totalAmount)}</span>
              </div>
            </div>
          </div>

          {/* Pay button - full width with card styling */}
          <div className="mt-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-4">
            <button
              disabled={loading || totalAmount === 0}
              className={`w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-semibold py-4 px-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center gap-2 ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
              onClick={handlePayment}
            >
              {loading ? (
                <>
                  <i className="fa fa-spinner fa-spin text-lg"></i>
                  <span className="text-lg">Processing...</span>
                </>
              ) : (
                <>
                  <i className="fa fa-credit-card text-lg"></i>
                  <span className="text-lg">Pay {is_money(totalAmount)} {gateway?.displayName ? `via ${gateway.displayName}` : ""}</span>
                </>
              )}
            </button>
          </div>
        </>
      )}

      {feeItems && feeItems.length === 0 && (
        <p className="text-sm text-amber-600">
          No acceptance fee has been configured for this session. Please contact the administrator.
        </p>
      )}

      {/* Waiver Button */}
      {feeItems && feeItems.length > 0 && (
        <div className="mt-2 text-center">
          <button
            type="button"
            onClick={() => { setShowWaiverModal(true); setWaiverCodes(["", "", "", "", "", ""]); }}
            className="inline-flex items-center gap-2 text-sm font-medium text-amber-700 hover:text-amber-900 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-lg px-4 py-2 transition-all duration-200"
          >
            <FiGift className="h-4 w-4" />
            Have a Waiver Code?
          </button>
        </div>
      )}

      {/* Waiver Modal */}
      <ReactModal
        isOpen={showWaiverModal}
        onRequestClose={() => setShowWaiverModal(false)}
        className="ReactModal"
        overlayClassName="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center px-4 z-50"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
          className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden"
        >
          <div className="relative bg-gradient-to-r from-amber-500 via-amber-600 to-orange-600 px-6 py-5 rounded-t-xl">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-white/20 backdrop-blur-sm rounded-lg">
                <FiGift className="h-6 w-6 text-white" />
              </div>
              <div className="flex-1">
                <h5 className="text-lg font-semibold text-white">Apply Waiver Code</h5>
                <p className="text-amber-100 text-sm mt-0.5">Enter your code to waive acceptance fee</p>
              </div>
              <button onClick={() => setShowWaiverModal(false)} className="p-2 rounded-lg hover:bg-white/20 transition-colors">
                <FiX className="h-5 w-5 text-white" />
              </button>
            </div>
          </div>
          <div className="p-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
              <div className="flex items-start gap-2">
                <FiInfo className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-blue-800">Enter your 6-character waiver code to bypass acceptance fee payment.</p>
              </div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-amber-900">Acceptance Fee</span>
                <span className="text-xl font-bold text-neutral-900">{is_money(totalAmount)}</span>
              </div>
            </div>
            <div className="mb-6">
              <label className="block text-sm font-medium text-neutral-700 text-center mb-4">Enter Waiver Code</label>
              <div className="flex justify-center gap-2.5">
                {waiverCodes.map((code, index) => (
                  <input
                    key={index}
                    ref={(el) => (waiverInputRefs.current[index] = el)}
                    type="text"
                    maxLength="1"
                    value={code}
                    onChange={(e) => handleWaiverCodeChange(index, e.target.value)}
                    onKeyDown={(e) => handleWaiverKeyDown(index, e)}
                    onPaste={(e) => handleWaiverPaste(e)}
                    className="w-12 h-14 text-center text-xl font-bold font-mono uppercase rounded-lg border-2 border-neutral-300 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all duration-200 bg-white text-neutral-900"
                    autoFocus={index === 0}
                  />
                ))}
              </div>
            </div>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={handleApplyWaiver}
                disabled={waiverLoading}
                className={`flex-1 inline-flex items-center justify-center gap-2 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white font-semibold py-3 px-6 rounded-lg shadow-md transition-all duration-200 ${waiverLoading ? "opacity-70 cursor-not-allowed" : ""}`}
              >
                {waiverLoading ? (
                  <><i className="fa fa-spinner fa-spin"></i> Applying...</>
                ) : (
                  <><FiGift className="h-4 w-4" /> Apply Waiver</>
                )}
              </button>
              <button
                type="button"
                onClick={() => setShowWaiverModal(false)}
                className="px-6 py-3 border border-neutral-300 rounded-lg text-neutral-700 font-medium hover:bg-neutral-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </motion.div>
      </ReactModal>
    </div>
  );
};

export default PayAcceptance;
